import {
  deriveWorkPhase,
  isSessionOpen as isPhaseOpen,
  type ScheduleEntry,
  type WorkPhase,
} from '@myapp/contract';
import { formatTimeHHmm } from './geometry';

const MS_PER_MINUTE = 60_000;

/**
 * The window a human means by "der Termin" — Arbeit only, Anfahrt and Abfahrt excluded.
 *
 * Not `startsAt`/`endsAt`: those are the **occupancy** block, which is what a calendar *draws* and
 * how long the van is busy. Every clock time a user *reads* comes from here instead. Mixing the two
 * up shows a Termin travel-minutes earlier than the form that created it, with no compile error.
 *
 * `actual ?? planned` per component, matching the server's row-local COALESCE — a started
 * appointment reports when it really began, not when it was meant to.
 *
 * The end is deliberately **not** `actualEndsAt`: that is the technician's self-reported `HH:mm`
 * and may legitimately diverge from the durations (see `ScheduleEntrySchema`). Work start plus work
 * minutes is exactly where the day board's Abfahrt strip begins, so the two views cannot drift.
 *
 * ABSENCE and BREAK carry no travel or departure, so for them this collapses to the block itself.
 */
export function workWindow(entry: ScheduleEntry): { startsAt: string; endsAt: string } {
  const startsAt = entry.actualWorkStartsAt ?? entry.plannedWorkStartsAt;
  const minutes = entry.actualWorkMinutes ?? entry.plannedWorkMinutes;

  return {
    startsAt,
    endsAt: new Date(new Date(startsAt).getTime() + minutes * MS_PER_MINUTE).toISOString(),
  };
}

/** German label for an OPEN phase — what a ringed block prints and announces. */
const PHASE_LABEL: Record<WorkPhase, string | null> = {
  ANFAHRT: 'Anfahrt läuft',
  ARBEIT: 'Läuft',
  ABFAHRT: 'Abfahrt läuft',
  DONE: null,
};

export interface BlockTimes {
  /** The derived lifecycle phase; `null` until the first tap. */
  phase: WorkPhase | null;
  /** German label for an OPEN phase, `null` otherwise. */
  phaseLabel: string | null;
  /** Work itself is running — the `Läuft` marker, and the Anfahrt is drawn as done. */
  isInProgress: boolean;
  /** Any of the three phases is open — the pulsing ring. */
  isSessionOpen: boolean;
  /** `actual ?? planned` per component — how the block is *divided*. */
  travelMinutes: number;
  workMinutes: number;
  departureMinutes: number;
  /** When work starts, actual preferred — what the block prints, never what positions it. */
  workStartsAt: string;
  /** `workStartsAt` as local `HH:mm`. */
  timeLabel: string;
  /**
   * How far the open phase has run past the edge it was planned to end at, as of `nowMs`. Already
   * added to that phase's minutes above; `0` without a clock, before that edge, or once the
   * session closed.
   */
  overrunMinutes: number;
  /**
   * Where a calendar should draw the block from. `entry.startsAt`, except during the Anfahrt: the
   * server only moves the block once work starts, so a technician who left at 16:35 for a 17:15
   * block is anchored here at 16:35 — the strip runs from when they really left.
   */
  liveStartsAt: string;
  /** Where a calendar should draw the block to — `liveStartsAt` plus the three live durations. */
  liveEndsAt: string;
}

/**
 * Everything a calendar block says about *when*, resolved once for both calendars.
 *
 * The three durations are `COALESCE(actual, planned)` per component, exactly as the database
 * derives the occupied `startsAt`/`endsAt` — row-locally, with status never consulted. That is what
 * keeps the strips inside a block in step with the box around them: both read the same rule. An
 * earlier worker-only copy gated the actuals on "completed or in progress", which since the taps
 * model produces the same numbers in every reachable state and one day would not.
 *
 * The phase comes from `deriveWorkPhase`, so a board reading `phase === 'ANFAHRT'` sees the
 * technician driving out, which `actualWorkStartsAt && !actualEndsAt` alone never could.
 *
 * **Live edges.** An open phase has no actual minutes yet — they are derived when it closes — so
 * the server's `startsAt`/`endsAt` stay planned while the technician is still on site at 11:40 for
 * a job planned to 11:00. Given `nowMs`, the open phase is stretched to the clock: its minutes grow
 * by the overrun and `liveEndsAt` moves out by the same amount, so the block keeps up with the
 * technician instead of claiming the van is free. Only the open phase grows, and only forward: a
 * phase that has not reached its planned end is left at the plan, since "shorter than planned" is
 * not known until it closes.
 *
 * The Anfahrt is the one phase whose *start* the server does not know yet either (it moves the
 * block on `actualWorkStartsAt`, not on `travelStartedAt`), so during ANFAHRT the block is anchored
 * at `travelStartedAt` — earlier or later than planned — and the strip runs from there to the
 * planned work start, or to now once that has passed. The work strip stays where it was planned:
 * the 17:30 agreed with the Mieter is still the appointment until the technician taps "Arbeit
 * starten", whichever way the drive went.
 */
export function resolveBlockTimes(entry: ScheduleEntry, nowMs?: number): BlockTimes {
  const phase = deriveWorkPhase(entry);
  const workStartsAt = workWindow(entry).startsAt;

  let travelMinutes = entry.actualTravelMinutes ?? entry.plannedTravelMinutes;
  let workMinutes = entry.actualWorkMinutes ?? entry.plannedWorkMinutes;
  let departureMinutes = entry.actualDepartureMinutes ?? entry.plannedDepartureMinutes;

  let startMs = new Date(entry.startsAt).getTime();
  let overrunMinutes = 0;

  const travelStartedMs = entry.travelStartedAt ? new Date(entry.travelStartedAt).getTime() : NaN;

  if (phase === 'ANFAHRT' && !Number.isNaN(startMs) && !Number.isNaN(travelStartedMs)) {
    // The planned work start is where the strip was going to end; it ends there, at the real
    // departure if that is later still, or at the clock once the plan has been overrun.
    const plannedWorkStartMs = startMs + travelMinutes * MS_PER_MINUTE;
    const travelEndMs = Math.max(plannedWorkStartMs, travelStartedMs, nowMs ?? 0);
    travelMinutes = toMinutes(travelEndMs - travelStartedMs);
    overrunMinutes = toMinutes(travelEndMs - plannedWorkStartMs);
    startMs = travelStartedMs;
  } else if (nowMs != null && !Number.isNaN(startMs)) {
    overrunMinutes = resolveOverrun(startMs, phase, nowMs, [travelMinutes, workMinutes, departureMinutes]);
    if (phase === 'ARBEIT') workMinutes += overrunMinutes;
    else if (phase === 'ABFAHRT') departureMinutes += overrunMinutes;
  }

  const liveStartsAt = Number.isNaN(startMs) ? entry.startsAt : new Date(startMs).toISOString();
  const liveEndsAt = Number.isNaN(startMs)
    ? entry.endsAt
    : new Date(startMs + (travelMinutes + workMinutes + departureMinutes) * MS_PER_MINUTE).toISOString();

  return {
    phase,
    phaseLabel: phase ? PHASE_LABEL[phase] : null,
    isInProgress: phase === 'ARBEIT',
    isSessionOpen: isPhaseOpen(phase),
    travelMinutes,
    workMinutes,
    departureMinutes,
    workStartsAt,
    timeLabel: formatTimeHHmm(workStartsAt),
    overrunMinutes,
    liveStartsAt,
    liveEndsAt,
  };
}

const toMinutes = (ms: number) => Math.max(0, Math.floor(ms / MS_PER_MINUTE));

/**
 * Whole minutes the open ARBEIT or ABFAHRT phase has run past the edge it was drawn to. The edges
 * are laid out from `startMs` with the same durations the strips use, so the overrun is measured
 * against exactly what the block shows. (The Anfahrt is handled by its caller — its start moves.)
 */
function resolveOverrun(
  startMs: number,
  phase: WorkPhase | null,
  nowMs: number,
  [travel, work, departure]: [number, number, number],
): number {
  if (phase !== 'ARBEIT' && phase !== 'ABFAHRT') return 0;

  const workEndMs = startMs + (travel + work) * MS_PER_MINUTE;
  const departureEndMs = workEndMs + departure * MS_PER_MINUTE;
  const phaseEndMs = phase === 'ARBEIT' ? workEndMs : departureEndMs;

  return toMinutes(nowMs - phaseEndMs);
}
